/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
/* eslint-enable @typescript-eslint/ban-ts-comment */
/**
 * German Salary & JobAuto Calculator
 * Based on 2025/2026 German tax and social security regulations
 *
 * Uses official BMF Programmablaufplan (PAP) for 2026
 * https://www.bmf-steuerrechner.de/
 */

// @ts-expect-error - taxjs doesn't have proper TypeScript support
import { Lohnsteuer2026 } from 'taxjs/dist/es2015/Lohnsteuer2026Big.js';
import Big from 'big.js';

// 2025 Social Security Contribution Ceilings (Beitragsbemessungsgrenzen)
export const BBG_2025 = {
  krankenPflege: 66150, // Annual ceiling for health/care insurance
  renteArbeitslos: 96600, // Annual ceiling for pension/unemployment (West)
  renteArbeitslosOst: 96600, // Unified since 2025
}

// Monthly ceilings
export const BBG_MONTHLY_2025 = {
  krankenPflege: BBG_2025.krankenPflege / 12, // 5512.50
  renteArbeitslos: BBG_2025.renteArbeitslos / 12, // 8050
}

// 2025 Social Security Rates (employee portion)
export const SV_RATES_2025 = {
  krankenversicherung: 7.3, // Basic rate
  krankenZusatzbeitrag: 1.7, // Average additional contribution
  pflegeversicherung: 1.7, // Basic rate
  pflegeKinderlos: 0.6, // Additional for childless 23+
  rentenversicherung: 9.3,
  arbeitslosenversicherung: 1.3,
}

// Church tax rates by Bundesland
export const KIRCHENSTEUER_RATES: Record<string, number> = {
  'Baden-Württemberg': 8,
  'Bayern': 8,
  'Berlin': 9,
  'Brandenburg': 9,
  'Bremen': 9,
  'Hamburg': 9,
  'Hessen': 9,
  'Mecklenburg-Vorpommern': 9,
  'Niedersachsen': 9,
  'Nordrhein-Westfalen': 9,
  'Rheinland-Pfalz': 9,
  'Saarland': 9,
  'Sachsen': 9,
  'Sachsen-Anhalt': 9,
  'Schleswig-Holstein': 9,
  'Thüringen': 9,
}

export const BUNDESLAENDER = Object.keys(KIRCHENSTEUER_RATES)

export interface PersonalTaxInfo {
  steuerklasse: 1 | 2 | 3 | 4 | 5 | 6
  kirchensteuer: boolean
  bundesland: string
  kinderfreibetraege: number // 0, 0.5, 1, 1.5, 2, etc.
  kinderlosUeber23: boolean // For Pflegeversicherung surcharge
}

export interface PrivateInsuranceInfo {
  isPrivate: boolean
  monthlyKVPremium: number // Employee's private KV premium
  monthlyPVPremium: number // Employee's private PV premium
  arbeitgeberzuschussKV: number // Employer contribution to KV (max ~421€)
  arbeitgeberzuschussPV: number // Employer contribution to PV (max ~88€)
}

export interface SalaryInfo {
  bruttoMonatlich: number // Monthly gross salary
  bavArbeitnehmer: number // BAV employee contribution (Gehaltsverzicht)
  bavArbeitgeber: number // BAV employer contribution (AG-Zuschuss)
  sonstigeGeldwerteVorteile: number // Other benefits in kind
  privateInsurance?: PrivateInsuranceInfo // Private health insurance info
  kvZusatzbeitrag?: number // Kassenindividual KV Zusatzbeitrag (%) — overrides SV_RATES_2025 default
}

export interface JobAutoInfo {
  bruttolistenpreis: number // Car's gross list price
  entfernungKm: number // One-way commute distance in km
  arbeitstageProMonat: number // Working days per month (for 0.002% rule)
  gehaltsumwandlung: number // Salary sacrifice amount
  gwvPercentage: 0.25 | 0.5 | 1.0 // Explicitly selected gwV percentage
}

export interface SalaryBreakdown {
  brutto: number
  geldwerterVorteil: number
  steuerlichesBrutto: number // Brutto + Geldwerter Vorteil (for tax)
  svBrutto: number // Brutto for social security (after Gehaltsumwandlung)
  
  // Taxes
  lohnsteuer: number
  solidaritaetszuschlag: number
  kirchensteuer: number
  
  // Social Security
  krankenversicherung: number
  pflegeversicherung: number
  rentenversicherung: number
  arbeitslosenversicherung: number
  
  // Private Insurance (direct deductions)
  privateKVPremium: number
  privatePVPremium: number
  
  // Totals
  gesamtAbzuege: number
  nettoVerdienst: number // Before Netto-Abzüge (gwV, BAV subtracted, AG-Zuschuss added)
  netto: number // Final payout (Auszahlungsbetrag)
  
  // Additional info
  bavFreibetrag: number // Used BAV tax-free amount
  arbeitgeberzuschussKV: number // Employer contribution to private KV
  arbeitgeberzuschussPV: number // Employer contribution to private PV
}

/**
 * Calculate the geldwerter Vorteil (benefit in kind) for a JobAuto
 */
export function calculateGeldwerterVorteil(
  auto: JobAutoInfo
): { einProzent: number; pendler: number; gesamt: number } {
  const bruttolistenpreisForCalc = auto.bruttolistenpreis
  
  // Use explicitly selected gwV percentage
  const einProzentRate = auto.gwvPercentage
  
  // Calculate gwV using selected percentage
  const einProzent = Math.round(bruttolistenpreisForCalc * (einProzentRate / 100))
  
  // Commute benefit using 0.03% standard rule (Pauschalregel)
  // For EVs/PHEVs with reduced gwvPercentage (0.25% or 0.5%), the same factor
  // applies to the commute benefit: 0.03% × gwvFaktor × BLP × km
  // e.g. 0.25% EV, 40.000 BLP, 20km → 40.000 × 0.25 × 0.0003 × 20 = 60 EUR
  // Note: arbeitstageProMonat is NOT used for the 0.03% Pauschalregel (only for 0.002% Einzelfahrtmethode)
  let pendler = 0
  if (auto.entfernungKm > 0) {
    pendler = Math.round(bruttolistenpreisForCalc * einProzentRate * 0.0003 * auto.entfernungKm)
  }
  
  return {
    einProzent,
    pendler,
    gesamt: einProzent + pendler,
  }
}

/**
 * German income tax calculation using official BMF 2026 Lohnsteuer PAP
 * This uses the official Programmablaufplan from the German Finance Ministry
 */
export function calculateLohnsteuer(
  jahresBrutto: number,
  steuerklasse: number,
  kinderfreibetraege: number,
  isPrivateInsurance: boolean = false
): { lohnsteuer: number; solidaritaetszuschlag: number } {
  const tax = new Lohnsteuer2026();
  tax.initInputs();
  
  // Monthly calculation (LZZ=2 for monthly)
  tax.setLZZ(2);
  tax.setSTKL(steuerklasse);
  
  // Convert annual to monthly gross in cents
  const monatlichBruttoCent = Math.round((jahresBrutto / 12) * 100);
  tax.setRE4(new Big(monatlichBruttoCent));
  
  // Set child allowances (converted to count)
  // kinderfreibetraege: 0.5 = 1 child shared, 1 = 1 child full, etc.
  const kinderCount = Math.floor(kinderfreibetraege);
  if (kinderCount > 0) {
    tax.setZKF(new Big(kinderCount));
  }
  
  // Private insurance flag
  if (isPrivateInsurance) {
    tax.setPKV(1);
  }
  
  // Calculate
  tax.calculate();
  
  // Get monthly results in cents
  const lstlzzCent = tax.getLSTLZZ().toNumber();
  const solzlzzCent = tax.getSOLZLZZ().toNumber();
  
  // Convert from cents to euros and return annual values
  const lohnsteuerMonatlich = lstlzzCent / 100;
  const solidaritaetszuschlagMonatlich = solzlzzCent / 100;
  
  return {
    lohnsteuer: Math.round(lohnsteuerMonatlich * 12),
    solidaritaetszuschlag: Math.round(solidaritaetszuschlagMonatlich * 12)
  };
}

/**
 * Calculate full salary breakdown
 */
export function calculateSalaryBreakdown(
  personal: PersonalTaxInfo,
  salary: SalaryInfo,
  autos?: JobAutoInfo[]
): SalaryBreakdown {
  const brutto = salary.bruttoMonatlich
  
  // Calculate Geldwerter Vorteil for all cars
  let geldwerterVorteil = salary.sonstigeGeldwerteVorteile
  let gehaltsumwandlung = 0
  
  if (autos && autos.length > 0) {
    autos.forEach(auto => {
      const autoVorteil = calculateGeldwerterVorteil(auto)
      geldwerterVorteil += autoVorteil.gesamt
      gehaltsumwandlung += auto.gehaltsumwandlung
    })
  }
  
  // BAV Freibetrag (§3 Nr.63 EStG): steuer- und SV-frei bis 8% der BBG RV
  // 2025: 8% × 96.600€/Jahr = 7.728€/Jahr = 644€/Monat
  const bavSteuerFreibetrag = Math.min(salary.bavArbeitnehmer, BBG_MONTHLY_2025.renteArbeitslos * 0.08)
  const bavSVFreibetrag = Math.min(salary.bavArbeitnehmer, BBG_MONTHLY_2025.krankenPflege * 0.04)
  
  // Steuerliches Brutto = Brutto - Gehaltsumwandlung - BAV (steuerfrei) + Geldwerter Vorteil
  const steuerlichesBrutto = brutto - gehaltsumwandlung - bavSteuerFreibetrag + geldwerterVorteil
  
  // SV-Brutto = Brutto - Gehaltsumwandlung - BAV (SV-frei)
  const svBrutto = Math.max(0, brutto - gehaltsumwandlung - bavSVFreibetrag)
  
  // Calculate annual for tax
  const jahresSteuerBrutto = steuerlichesBrutto * 12
  const isPrivate = salary.privateInsurance?.isPrivate || false
  const taxResult = calculateLohnsteuer(
    jahresSteuerBrutto,
    personal.steuerklasse,
    personal.kinderfreibetraege,
    isPrivate
  )
  const lohnsteuer = Math.round(taxResult.lohnsteuer / 12)
  const solidaritaetszuschlag = Math.round(taxResult.solidaritaetszuschlag / 12)
  
  // Kirchensteuer
  let kirchensteuer = 0
  if (personal.kirchensteuer) {
    const rate = KIRCHENSTEUER_RATES[personal.bundesland] || 9
    kirchensteuer = Math.round(lohnsteuer * (rate / 100))
  }
  
  // Private or statutory insurance (already declared above)
  let krankenversicherung = 0
  let pflegeversicherung = 0
  let privateKVPremium = 0
  let privatePVPremium = 0
  let arbeitgeberzuschussKV = 0
  let arbeitgeberzuschussPV = 0
  
  if (isPrivate) {
    // Private insurance: employee pays premium directly, no percentage deduction
    privateKVPremium = salary.privateInsurance?.monthlyKVPremium || 0
    privatePVPremium = salary.privateInsurance?.monthlyPVPremium || 0
    arbeitgeberzuschussKV = salary.privateInsurance?.arbeitgeberzuschussKV || 0
    arbeitgeberzuschussPV = salary.privateInsurance?.arbeitgeberzuschussPV || 0
    // No KV/PV percentage deduction
    krankenversicherung = 0
    pflegeversicherung = 0
  } else {
    // Statutory insurance: percentage-based deductions
    // Use the payslip-extracted Kassenzusatzbeitrag if provided; fall back to the 2025 average.
    const kvBeitragsbasis = Math.min(svBrutto, BBG_MONTHLY_2025.krankenPflege)
    const kvZusatz = salary.kvZusatzbeitrag ?? SV_RATES_2025.krankenZusatzbeitrag
    const kvRate = SV_RATES_2025.krankenversicherung + kvZusatz
    krankenversicherung = Math.round(kvBeitragsbasis * (kvRate / 100))
    
    // Pflegeversicherung
    let pvRate = SV_RATES_2025.pflegeversicherung
    if (personal.kinderlosUeber23) {
      pvRate += SV_RATES_2025.pflegeKinderlos
    }
    // Reduction for parents with multiple children (simplified)
    if (personal.kinderfreibetraege >= 2) {
      pvRate -= 0.25 * Math.min(personal.kinderfreibetraege - 1, 4)
    }
    pvRate = Math.max(0.9, pvRate) // Minimum rate
    pflegeversicherung = Math.round(kvBeitragsbasis * (pvRate / 100))
  }
  
  // Rentenversicherung (applies to both private and statutory)
  const rvBeitragsbasis = Math.min(svBrutto, BBG_MONTHLY_2025.renteArbeitslos)
  const rentenversicherung = Math.round(rvBeitragsbasis * (SV_RATES_2025.rentenversicherung / 100))
  
  // Arbeitslosenversicherung (applies to both private and statutory)
  const arbeitslosenversicherung = Math.round(rvBeitragsbasis * (SV_RATES_2025.arbeitslosenversicherung / 100))
  
  // Total deductions (taxes + SV only, BAV is separate Netto-Abzug)
  const gesamtAbzuege = 
    lohnsteuer +
    solidaritaetszuschlag +
    kirchensteuer +
    krankenversicherung +
    pflegeversicherung +
    rentenversicherung +
    arbeitslosenversicherung +
    privateKVPremium + // Private insurance premiums
    privatePVPremium
  
  // Netto-Verdienst (wie auf Gehaltsabrechnung):
  // = Gesamt-Brutto - Steuerabzüge - SV-Abzüge
  // Gesamt-Brutto = Brutto - Gehaltsumwandlung + gwV
  // Note: gwV is added to Gesamt-Brutto (for tax), then subtracted in Netto-Abzüge
  const gesamtBrutto = brutto - gehaltsumwandlung + geldwerterVorteil
  const nettoVerdienst = gesamtBrutto - gesamtAbzuege
  
  // Finaler Auszahlungsbetrag (nach Netto-Abzügen):
  // = Netto-Verdienst - gwV (Phantom-Einkommen) - BAV + AG-Zuschüsse
  const arbeitgeberzuschuss = (salary.privateInsurance?.arbeitgeberzuschussKV || 0) + 
                              (salary.privateInsurance?.arbeitgeberzuschussPV || 0)
  const netto = nettoVerdienst - geldwerterVorteil - salary.bavArbeitnehmer + arbeitgeberzuschuss
  
  return {
    brutto,
    geldwerterVorteil,
    steuerlichesBrutto,
    svBrutto,
    lohnsteuer,
    solidaritaetszuschlag,
    kirchensteuer,
    krankenversicherung,
    pflegeversicherung,
    rentenversicherung,
    arbeitslosenversicherung,
    privateKVPremium,
    privatePVPremium,
    gesamtAbzuege,
    nettoVerdienst,
    netto,
    bavFreibetrag: bavSteuerFreibetrag,
    arbeitgeberzuschussKV,
    arbeitgeberzuschussPV,
  }
}

/**
 * Calculate the true cost of JobAutos
 */
export function calculateJobAutoCost(
  personal: PersonalTaxInfo,
  salary: SalaryInfo,
  autos: JobAutoInfo[]
): {
  ohneAuto: SalaryBreakdown
  mitAuto: SalaryBreakdown
  nettoReduktion: number
  wahreMonatskosten: number
  rentenVerlustMonatlich: number
  arbeitgeberErsparnis: number
} {
  const ohneAuto = calculateSalaryBreakdown(personal, salary, [])
  const mitAuto = calculateSalaryBreakdown(personal, salary, autos)
  
  const nettoReduktion = ohneAuto.netto - mitAuto.netto
  
  // The true monthly cost is the net salary reduction
  const wahreMonatskosten = nettoReduktion
  
  // Impact on future pension (rough estimate)
  // Each month of lower SV-Brutto means lower pension points
  const rentenPunkteVerlust = (ohneAuto.svBrutto - mitAuto.svBrutto) / (BBG_MONTHLY_2025.renteArbeitslos * 12) * 12
  // Each pension point is worth ~39€/month in pension (2025)
  const rentenVerlustMonatlich = rentenPunkteVerlust * 39.32 / 12
  
  // Employer also saves on social security (roughly same amount as employee)
  const arbeitgeberErsparnis = 
    (ohneAuto.krankenversicherung - mitAuto.krankenversicherung) +
    (ohneAuto.pflegeversicherung - mitAuto.pflegeversicherung) +
    (ohneAuto.rentenversicherung - mitAuto.rentenversicherung) +
    (ohneAuto.arbeitslosenversicherung - mitAuto.arbeitslosenversicherung)
  
  return {
    ohneAuto,
    mitAuto,
    nettoReduktion,
    wahreMonatskosten,
    rentenVerlustMonatlich,
    arbeitgeberErsparnis,
  }
}
